import { applicationSessionStorage } from '@application/application_storage_session';
import { NOT_YET_ADDED_TRANSLATIONS } from './not_yet_added_translation_dictionary';
import type {
	TranslationDictionaryPatientWeb,
	TranslationDictionaryCommonFrontend,
	TranslationDictionaryPatient,
} from '@visiba/translation-dictionaries';

/**
 * # Internationalization and localization
 */
export namespace I18nAndL10n {
	type CombinedTranslationDictionary = typeof NOT_YET_ADDED_TRANSLATIONS &
		TranslationDictionaryPatient &
		TranslationDictionaryPatientWeb &
		TranslationDictionaryCommonFrontend;

	export type TranslationDictionary = Partial<CombinedTranslationDictionary>;

	export type LocaleKey = keyof TranslationDictionary;

	export type AvailableVisibaLocales =
		| 'sv' // Swedish
		| 'en-gb' // English (United Kingdom)
		| 'da' // Danish
		| 'de' // German
		| 'nb' // Norwegian Bokmål
		| 'fi' // Finnish
		| 'ar' // Arabic
		| 'fa' // Persian
		| 'hr' // Croatian
		| 'nl' // Dutch
		| 'pt-br'; // Portuguese (Brazil)

	let selectedLocale: AvailableVisibaLocales | null = null;
	let translations: Partial<CombinedTranslationDictionary> = {};

	function setLangCode(langCode: AvailableVisibaLocales): AvailableVisibaLocales {
		document.documentElement.lang = langCode;

		return langCode;
	}

	export function convertExternalLocaleToAvailableLocale(external: string): AvailableVisibaLocales {
		const externalLowercase = external.toLocaleLowerCase();

		// Needs to convert some Visiba locales due to not matching directly to Dayjs
		if (externalLowercase === 'en') return 'en-gb';
		if (externalLowercase === 'no') return 'nb';

		const availableLocales: I18nAndL10n.AvailableVisibaLocales[] = [
			'sv',
			'nb',
			'fi',
			'da',
			'de',
			'en-gb',
			'ar',
			'fa',
			'hr',
			'nl',
			'pt-br',
		];

		if (availableLocales.includes(externalLowercase as I18nAndL10n.AvailableVisibaLocales)) {
			return externalLowercase as I18nAndL10n.AvailableVisibaLocales;
		}

		throw new Error(`${external} is unsupported`);
	}

	export function updateLangCode(external: string): void {
		try {
			selectedLocale = setLangCode(convertExternalLocaleToAvailableLocale(external));
		} catch (error) {
			console.error(error);
			selectedLocale = setLangCode('sv');
		}
	}

	export function getSelectedLangCode(): AvailableVisibaLocales {
		if (selectedLocale == null) throw new Error('Locale was never selected');

		return selectedLocale;
	}

	export function insertTranslations(newTranslationSet: { [key: string]: string }): void {
		try {
			translations = {
				...NOT_YET_ADDED_TRANSLATIONS,
				...newTranslationSet,
			};
		} catch {
			throw new Error('there was an Error trying to insert translations');
		}
	}

	export function override(overrideData: TranslationDictionary): void {
		translations = { ...translations, ...overrideData };
	}

	export function derived(translationSet: TranslationDictionary): void {
		translations = { ...translationSet, ...translations };
	}

	export function translate(key: LocaleKey, replaceArguments: { [key: string]: string } | null = null, ignoreWarning = false): string {
		const shouldShowAlias = applicationSessionStorage.get('showAlias');
		if (shouldShowAlias) return key;

		let translation = translations[key];
		if (!translation) return ignoreWarning ? '' : `! ! ! ${key} ! ! !`;

		if (replaceArguments) {
			for (const replace in replaceArguments) {
				translation = translation.replace(`{{${replace}}}`, replaceArguments[replace]);
			}
		}

		return translation;
	}
}
